import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import AnnouncementBar from './AnnouncementBar';
import { DienstenDropdown, BlogDropdown, VoorWieDropdown, voorWieItems, blogItems, dienstenItems } from './NavDropdowns';
import BesparingCalculator from './BesparingCalculator';

const HEADER_OFFSET = 132;
type DropdownKey = 'diensten' | 'voor-wie' | 'blog';

function scrollToSection(sectionId: string) {
  const el = document.querySelector(sectionId);
  if (el) {
    const top = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState<DropdownKey | null>(null);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey | null>(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleDropdownEnter = (key: DropdownKey) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(key);
  };

  const handleDropdownLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
  };

  const handleSectionClick = (sectionId: string) => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      navigate('/');
      setTimeout(() => scrollToSection(sectionId), 350);
    }
  };

  const closeMobile = () => { setMobileOpen(false); setMobileSubOpen(null); };
  const toggleMobileSub = (key: DropdownKey) => setMobileSubOpen(v => v === key ? null : key);

  const textColor = scrolled ? 'text-[#0A1628]/75 hover:text-[#3B9EE8]' : 'text-white/85 hover:text-white';
  const hoverBg = scrolled ? 'hover:bg-[#3B9EE8]/8' : '';

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <AnnouncementBar />

      <nav className={`transition-all duration-300 ${scrolled ? 'bg-white border-b border-[#0A1628]/10' : 'bg-transparent'}`}>

        {/* ─── DESKTOP NAV (lg+) ─── */}
        <div className="hidden lg:flex max-w-7xl mx-auto px-6 lg:px-10 items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center cursor-pointer flex-shrink-0">
            <img src="https://static.readdy.ai/image/15bdf5788918520e40e960585d4ea965/3037d4ed8e2d758e66815cdcd69eb3cd.png" alt="DUC - Direct uit China" className="h-[88px] w-auto object-contain" />
          </Link>

          {/* Desktop nav links */}
          <div className="flex items-center gap-0.5">
            <div className="relative flex items-center" onMouseEnter={() => handleDropdownEnter('diensten')} onMouseLeave={handleDropdownLeave}>
              <button onClick={() => handleSectionClick('#diensten')} className={`px-3 py-2 rounded-l-md text-sm font-500 transition-colors duration-200 cursor-pointer whitespace-nowrap ${textColor} ${hoverBg}`}>Diensten</button>
              <button onClick={() => setOpenDropdown(v => v === 'diensten' ? null : 'diensten')} className={`px-1 py-2 rounded-r-md text-sm transition-colors duration-200 cursor-pointer ${textColor} ${hoverBg}`}>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${openDropdown === 'diensten' ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>{openDropdown === 'diensten' && <DienstenDropdown onClose={() => setOpenDropdown(null)} onMouseEnter={() => { if (closeTimer.current) clearTimeout(closeTimer.current); }} onMouseLeave={handleDropdownLeave} />}</AnimatePresence>
            </div>

            <button onClick={() => handleSectionClick('#proces')} className={`px-3 py-2 rounded-md text-sm font-500 transition-colors duration-200 cursor-pointer whitespace-nowrap ${textColor} ${hoverBg}`}>Hoe het werkt</button>
            <Link to="/wie-zijn-wij" className={`px-3 py-2 rounded-md text-sm font-500 transition-colors duration-200 cursor-pointer whitespace-nowrap ${textColor} ${hoverBg}`}>Wie zijn wij</Link>

            <div className="relative flex items-center" onMouseEnter={() => handleDropdownEnter('voor-wie')} onMouseLeave={handleDropdownLeave}>
              <button onClick={() => handleSectionClick('#voor-wie')} className={`px-3 py-2 rounded-l-md text-sm font-500 transition-colors duration-200 cursor-pointer whitespace-nowrap ${textColor} ${hoverBg}`}>Voor wie</button>
              <button onClick={() => setOpenDropdown(v => v === 'voor-wie' ? null : 'voor-wie')} className={`px-1 py-2 rounded-r-md text-sm transition-colors duration-200 cursor-pointer ${textColor} ${hoverBg}`}>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${openDropdown === 'voor-wie' ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>{openDropdown === 'voor-wie' && <VoorWieDropdown onClose={() => setOpenDropdown(null)} onMouseEnter={() => { if (closeTimer.current) clearTimeout(closeTimer.current); }} onMouseLeave={handleDropdownLeave} />}</AnimatePresence>
            </div>

            <div className="relative flex items-center" onMouseEnter={() => handleDropdownEnter('blog')} onMouseLeave={handleDropdownLeave}>
              <Link to="/blog" className={`px-3 py-2 rounded-l-md text-sm font-500 transition-colors duration-200 cursor-pointer whitespace-nowrap ${textColor} ${hoverBg}`}>Blog</Link>
              <button onClick={() => setOpenDropdown(v => v === 'blog' ? null : 'blog')} className={`px-1 py-2 rounded-r-md text-sm transition-colors duration-200 cursor-pointer ${textColor} ${hoverBg}`}>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${openDropdown === 'blog' ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>{openDropdown === 'blog' && <BlogDropdown onClose={() => setOpenDropdown(null)} onMouseEnter={() => { if (closeTimer.current) clearTimeout(closeTimer.current); }} onMouseLeave={handleDropdownLeave} />}</AnimatePresence>
            </div>

            <button onClick={() => handleSectionClick('#faq')} className={`px-3 py-2 rounded-md text-sm font-500 transition-colors duration-200 cursor-pointer whitespace-nowrap ${textColor} ${hoverBg}`}>FAQ</button>
            <Link to="/contact" className={`px-3 py-2 rounded-md text-sm font-500 transition-colors duration-200 cursor-pointer whitespace-nowrap ${textColor} ${hoverBg}`}>Contact</Link>
          </div>

          {/* Desktop CTAs */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCalculatorOpen(true)}
              className={`flex items-center gap-1.5 border px-3 py-2 rounded-md text-xs font-600 transition-all duration-200 cursor-pointer whitespace-nowrap ${scrolled ? 'text-[#16a34a] border-[#16a34a]/30 hover:bg-[#16a34a]/8 hover:border-[#16a34a]/60' : 'text-white/85 border-white/25 hover:bg-white/10 hover:border-white/50'}`}
            >
              <i className="ri-calculator-line text-sm" />
              Bereken je besparing!
            </button>
            <a
              href="https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China."
              target="_blank" rel="nofollow noreferrer"
              className={`flex items-center gap-1.5 border px-3 py-2 rounded-md text-sm font-500 transition-all duration-200 cursor-pointer whitespace-nowrap ${scrolled ? 'text-[#0A1628]/70 hover:text-[#3B9EE8] border-[#0A1628]/20 hover:border-[#3B9EE8]/50' : 'text-white/80 hover:text-[#3B9EE8] border-white/20 hover:border-[#3B9EE8]/50'}`}
            >
              <i className="ri-whatsapp-line text-base" />
              WhatsApp
            </a>
            <button
              onClick={() => handleSectionClick('#contact')}
              className="bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-4 py-2.5 rounded-md text-sm font-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Gratis Prijscheck
            </button>
          </div>
        </div>

        {/* ─── MOBILE / TABLET NAV (< lg) ─── */}
        <div className="lg:hidden">
          {/* Row: Logo + Action buttons + WhatsApp + Hamburger */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 px-3 sm:px-5 md:px-7 h-16 sm:h-20 md:h-20">
            {/* Logo */}
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center cursor-pointer flex-shrink-0 mr-auto">
              <img
                src="https://static.readdy.ai/image/15bdf5788918520e40e960585d4ea965/3037d4ed8e2d758e66815cdcd69eb3cd.png"
                alt="DUC - Direct uit China"
                className="h-10 sm:h-12 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Bereken besparing knop */}
            <button
              onClick={() => setCalculatorOpen(true)}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-xs md:text-sm font-600 transition-all cursor-pointer whitespace-nowrap border flex-shrink-0 ${
                scrolled
                  ? 'text-[#16a34a] border-[#16a34a]/30 bg-[#16a34a]/6 hover:bg-[#16a34a]/12'
                  : 'text-white border-white/25 bg-white/8 hover:bg-white/15'
              }`}
            >
              <i className="ri-calculator-line text-sm md:text-base flex-shrink-0" />
              <span className="hidden sm:inline md:inline">Besparing</span>
            </button>

            {/* Offerte aanvragen knop */}
            <button
              onClick={() => navigate('/offerte')}
              className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-xs md:text-sm font-700 transition-all cursor-pointer whitespace-nowrap bg-[#3B9EE8] hover:bg-[#2D85C5] text-white flex-shrink-0"
            >
              <i className="ri-file-list-3-line text-sm md:text-base flex-shrink-0" />
              <span>Offerte</span>
            </button>

            {/* WhatsApp knop */}
            <a
              href="https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China."
              target="_blank"
              rel="nofollow noreferrer"
              className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg transition-all cursor-pointer flex-shrink-0 ${
                scrolled ? 'text-[#25D366] bg-[#25D366]/8 hover:bg-[#25D366]/15' : 'text-[#25D366] bg-white/8 hover:bg-white/15'
              }`}
              aria-label="WhatsApp"
            >
              <i className="ri-whatsapp-line text-lg md:text-xl" />
            </a>

            {/* Hamburger */}
            <button
              className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors flex-shrink-0 ${scrolled ? 'text-[#0A1628] hover:bg-[#0A1628]/6' : 'text-white hover:bg-white/10'}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu openen"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu-3'}-line text-xl`} />
            </button>
          </div>
        </div>

        {/* ─── MOBILE MENU DROPDOWN ─── */}
        <AnimatePresence>
          {mobileOpen && (
            <div className="lg:hidden bg-white border-t border-[#0A1628]/8 px-4 sm:px-6 pb-6 overflow-hidden max-h-[70vh] overflow-y-auto">
              <div className="flex flex-col gap-1 pt-3">

                <MobileAccordion label="Diensten" icon="ri-briefcase-4-line" isOpen={mobileSubOpen === 'diensten'} onToggle={() => toggleMobileSub('diensten')} onLabelClick={() => handleSectionClick('#diensten')}>
                  {dienstenItems.map(item => (
                    <Link key={item.label} to={item.href} onClick={closeMobile} className="flex items-center gap-2.5 py-2 text-sm text-[#0A1628]/65 hover:text-[#3B9EE8] transition-colors w-full text-left cursor-pointer">
                      <div className="w-6 h-6 flex items-center justify-center rounded-md bg-[#0A1628]/5 flex-shrink-0"><i className={`${item.icon} text-xs`} /></div>
                      {item.label}
                    </Link>
                  ))}
                </MobileAccordion>

                <button onClick={() => handleSectionClick('#proces')} className="flex items-center gap-3 text-[#0A1628]/80 hover:text-[#3B9EE8] text-sm font-500 text-left transition-colors cursor-pointer py-3 border-b border-[#0A1628]/6 w-full">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0A1628]/6 flex-shrink-0"><i className="ri-route-line text-xs text-[#0A1628]/60" /></div>
                  Hoe het werkt
                </button>

                <Link to="/wie-zijn-wij" onClick={closeMobile} className="flex items-center gap-3 text-[#0A1628]/80 hover:text-[#3B9EE8] text-sm font-500 text-left transition-colors cursor-pointer py-3 border-b border-[#0A1628]/6">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0A1628]/6 flex-shrink-0"><i className="ri-team-line text-xs text-[#0A1628]/60" /></div>
                  Wie zijn wij
                </Link>

                <MobileAccordion label="Voor wie" icon="ri-group-line" isOpen={mobileSubOpen === 'voor-wie'} onToggle={() => toggleMobileSub('voor-wie')} onLabelClick={() => handleSectionClick('#voor-wie')}>
                  {voorWieItems.map(item => (
                    <Link key={item.label} to={item.href} onClick={closeMobile} className="flex items-center gap-2.5 py-2 text-sm text-[#0A1628]/65 hover:text-[#3B9EE8] transition-colors w-full text-left cursor-pointer">
                      <div className="w-6 h-6 flex items-center justify-center rounded-md bg-[#0A1628]/5 flex-shrink-0"><i className={`${item.icon} text-xs`} /></div>
                      {item.label}
                    </Link>
                  ))}
                  <Link to="/voor-wie" onClick={closeMobile} className="mt-1 text-xs text-[#3B9EE8] font-600 flex items-center gap-1">Alle sectoren <i className="ri-arrow-right-line text-xs" /></Link>
                </MobileAccordion>

                <MobileAccordion label="Blog" icon="ri-newspaper-line" isOpen={mobileSubOpen === 'blog'} onToggle={() => toggleMobileSub('blog')} onLabelClick={() => { closeMobile(); navigate('/blog'); }}>
                  {blogItems.map(item => (
                    <Link key={item.href} to={item.href} onClick={closeMobile} className="flex items-center gap-2.5 py-2 text-sm text-[#0A1628]/65 hover:text-[#3B9EE8] transition-colors">
                      <div className="w-6 h-6 flex items-center justify-center rounded-md bg-[#0A1628]/5 flex-shrink-0"><i className={`${item.icon} text-xs`} /></div>
                      <span className="flex-1 min-w-0 truncate">{item.label}</span>
                      <span className="text-[#3B9EE8] text-xs ml-auto whitespace-nowrap pl-2">{item.price}</span>
                    </Link>
                  ))}
                  <Link to="/blog" onClick={closeMobile} className="mt-1 text-xs text-[#3B9EE8] font-600 flex items-center gap-1">Alle blogs <i className="ri-arrow-right-line text-xs" /></Link>
                </MobileAccordion>

                <Link to="/contact" onClick={closeMobile} className="flex items-center gap-3 text-[#0A1628]/80 hover:text-[#3B9EE8] text-sm font-500 text-left transition-colors cursor-pointer py-3 border-b border-[#0A1628]/6">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0A1628]/6 flex-shrink-0"><i className="ri-mail-send-line text-xs text-[#0A1628]/60" /></div>
                  Contact
                </Link>

                <button onClick={() => handleSectionClick('#faq')} className="flex items-center gap-3 text-[#0A1628]/80 hover:text-[#3B9EE8] text-sm font-500 text-left transition-colors cursor-pointer py-3 border-b border-[#0A1628]/6 w-full">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0A1628]/6 flex-shrink-0"><i className="ri-question-answer-line text-xs text-[#0A1628]/60" /></div>
                  FAQ
                </button>

                {/* Actieknoppen onderaan menu */}
                <div className="pt-4 pb-2 flex flex-col gap-3">
                  <button
                    onClick={() => { closeMobile(); setCalculatorOpen(true); }}
                    className="w-full flex items-center justify-center gap-2 border border-[#16a34a]/40 bg-[#16a34a]/6 text-[#16a34a] font-600 text-sm py-3 rounded-xl cursor-pointer transition-all hover:bg-[#16a34a]/12 whitespace-nowrap"
                  >
                    <i className="ri-calculator-line text-base" />
                    Bereken je besparing
                  </button>
                  <button
                    onClick={() => { closeMobile(); navigate('/offerte'); }}
                    className="w-full flex items-center justify-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-sm py-3 rounded-xl cursor-pointer transition-all whitespace-nowrap"
                  >
                    <i className="ri-file-list-3-line text-base" />
                    Gratis Offerte Aanvragen
                  </button>
                  <a
                    href="https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China."
                    target="_blank"
                    rel="nofollow noreferrer"
                    onClick={closeMobile}
                    className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BC5A] text-white font-600 text-sm py-3 rounded-xl cursor-pointer transition-all whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-base" />
                    WhatsApp Shubam
                  </a>
                </div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </nav>

      <BesparingCalculator isOpen={calculatorOpen} onClose={() => setCalculatorOpen(false)} />
    </div>
  );
}

interface MobileAccordionProps {
  label: string;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
  onLabelClick: () => void;
  children: React.ReactNode;
}

function MobileAccordion({ label, icon, isOpen, onToggle, onLabelClick, children }: MobileAccordionProps) {
  return (
    <div className="border-b border-[#0A1628]/6">
      <div className="flex items-center gap-3 py-3">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0A1628]/6 flex-shrink-0">
          <i className={`${icon} text-xs text-[#0A1628]/60`} />
        </div>
        <button onClick={onLabelClick} className="text-[#0A1628]/80 hover:text-[#3B9EE8] text-sm font-500 text-left transition-colors cursor-pointer flex-1">
          {label}
        </button>
        <button onClick={onToggle} className="w-7 h-7 flex items-center justify-center rounded-lg text-[#0A1628]/40 hover:text-[#3B9EE8] hover:bg-[#3B9EE8]/8 transition-all cursor-pointer flex-shrink-0">
          <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {isOpen && (
        <div className="pb-3 flex flex-col gap-0.5 pl-10">{children}</div>
      )}
    </div>
  );
}
